import React, { useState, useEffect } from "react";
import { Paper } from "@material-ui/core";
import { Spinner } from "react-bootstrap";
import { useAlert } from "react-alert";
import { baseUrl } from "../../../../services/config";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const EmployeeOffDays = () => {
  const authtoken = localStorage.getItem("userToken");
  const alert = useAlert();

  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());

  const [selectedDates, setSelectedDates] = useState(new Set());
  const [originalDates, setOriginalDates] = useState(new Set());
  const [allOffDays, setAllOffDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyMonthFilter = (allDates, year, month) => {
    const prefix = `${year}-${String(month + 1).padStart(2, "0")}-`;
    const thisMonth = new Set(allDates.filter((d) => d.startsWith(prefix)));
    setSelectedDates(new Set(thisMonth));
    setOriginalDates(new Set(thisMonth));
  };

  const fetchOffDays = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/emp-off-days`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await res.json();
      if (data.metadata?.success) {
        const all = data.payload.map((d) => d.off_date);
        setAllOffDays(all);
        applyMonthFilter(all, currentYear, currentMonth);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffDays();
  }, []);

  useEffect(() => {
    applyMonthFilter(allOffDays, currentYear, currentMonth);
  }, [currentYear, currentMonth]);

  const goMonth = (delta) => {
    let m = currentMonth + delta;
    let y = currentYear;
    if (m > 11) { m = 0; y += 1; }
    if (m < 0) { m = 11; y -= 1; }
    setCurrentMonth(m);
    setCurrentYear(y);
  };

  const goYear = (delta) => setCurrentYear((y) => y + delta);

  const fmtDate = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const toggleDate = (dateStr) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      next.has(dateStr) ? next.delete(dateStr) : next.add(dateStr);
      return next;
    });
  };

  const handleSave = async () => {
    const toAdd = [...selectedDates].filter((d) => !originalDates.has(d));
    const toRemove = [...originalDates].filter((d) => !selectedDates.has(d));
    if (toAdd.length === 0 && toRemove.length === 0) {
      alert.show("No changes to save.");
      return;
    }
    setSaving(true);
    try {
      if (toAdd.length > 0) {
        const res = await fetch(`${baseUrl}/emp-off-days`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authtoken}` },
          body: JSON.stringify({ dates: toAdd }),
        });
        const data = await res.json();
        if (!data.metadata?.success) {
          alert.error(data.metadata?.message || "Failed to add off days.");
          setSaving(false);
          return;
        }
      }
      if (toRemove.length > 0) {
        const res = await fetch(`${baseUrl}/emp-off-days/remove`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authtoken}` },
          body: JSON.stringify({ dates: toRemove }),
        });
        const data = await res.json();
        if (!data.metadata?.success) {
          alert.error("Failed to remove off days.");
          setSaving(false);
          return;
        }
      }
      alert.show("Off days saved successfully.");
      await fetchOffDays();
    } catch (err) {
      console.log(err);
      alert.error("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    const toRemove = [...originalDates];
    if (toRemove.length === 0) {
      setSelectedDates(new Set());
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${baseUrl}/emp-off-days/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authtoken}` },
        body: JSON.stringify({ dates: toRemove }),
      });
      const data = await res.json();
      if (data.metadata?.success) {
        alert.show("All off days cleared for this month.");
        await fetchOffDays();
      } else {
        alert.error("Failed to clear off days.");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();

  const renderCells = () => {
    const cells = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push(<div key={`e-${i}`} />);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = fmtDate(currentYear, currentMonth, d);
      const isOff = selectedDates.has(dateStr);
      const dayName = DAYS[new Date(currentYear, currentMonth, d).getDay()];
      cells.push(
        <div
          key={dateStr}
          onClick={() => toggleDate(dateStr)}
          style={{
            border: `2px solid ${isOff ? "var(--primary-btn-color)" : "#e0e0e0"}`,
            borderRadius: 8,
            padding: "10px 4px",
            cursor: "pointer",
            textAlign: "center",
            backgroundColor: isOff ? "var(--primary-btn-color)" : "#fff",
            color: isOff ? "#fff" : "#333",
            userSelect: "none",
            transition: "all 0.15s",
            minHeight: 70,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: "bold", lineHeight: 1.2 }}>{d}</div>
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>{dayName}</div>
        </div>
      );
    }
    return cells;
  };

  return (
    <Paper style={{ padding: 28, marginTop: 10, maxWidth: 900, margin: "10px auto" }}>
      <p style={{ color: "var(--primary-btn-color)", marginBottom: 20, fontSize: 13 }}>
        Click dates to mark them as off days, then save. Use <strong>Clear</strong> to remove all off days for this month.
      </p>

      {/* Navigation header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => goYear(-1)} className="btn btn-outline-secondary btn-sm" style={{ fontWeight: "bold" }}>«</button>
          <button onClick={() => goMonth(-1)} className="btn btn-outline-secondary btn-sm" style={{ fontWeight: "bold" }}>‹</button>
        </div>
        <h4 style={{ fontWeight: "bold", margin: 0, fontSize: 22 }}>
          {MONTHS[currentMonth]} {currentYear}
        </h4>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => goMonth(1)} className="btn btn-outline-secondary btn-sm" style={{ fontWeight: "bold" }}>›</button>
          <button onClick={() => goYear(1)} className="btn btn-outline-secondary btn-sm" style={{ fontWeight: "bold" }}>»</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          {/* Day-of-week headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: 10 }}>
            {DAYS.map((d) => (
              <div key={d} style={{ textAlign: "center", fontWeight: "bold", fontSize: 13, color: "#555" }}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
            {renderCells()}
          </div>
        </>
      )}

      {/* Footer */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 28 }}>
        <button
          onClick={handleClear}
          disabled={saving}
          className="btn btn-outline-secondary"
          style={{ minWidth: 90 }}
        >
          {saving ? <Spinner size="sm" animation="border" /> : "Clear"}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-success button_blue"
        >
          {saving ? <Spinner size="sm" animation="border" /> : "Save"}
        </button>
      </div>
    </Paper>
  );
};

export default EmployeeOffDays;
